import TemplateView from "../../../TemplateView";
import CPSelectorController from "../../add_tasks/cp_selector_controller";
import messageResource from "../../../messageResource";
import KeyDownHandler from "../../../key_down_handler";
import UserNameFormat from "../../../util/UserNameFormat";
import {KEY_CODE_ESC} from "../../../keyCodes";
import {arrayToSet} from "../../../atUtils";
import {smoothlyScrollDownToElement, setMouseDownOutsideHandler, showElementAt} from "../../../atBrowserUtils";
import ScrollUtils from '../../../util/ScrollUtils'
import {EntityTextLimits} from 'constants'

export default class BaseTaskSelector extends TemplateView {
    constructor($containerEl, {
        $scrollableContainerElement,
        selectionCallback,
        onHideCallback = () => {},
        isImportTasksMode = true,
        canSelectAllInCustomer = false,
        canSelectAllInProject = false
    }) {
        super($containerEl);

        this.$scrollableContainerElement = $scrollableContainerElement;
        this.selectionCallback = selectionCallback;
        this.onHideCallback = onHideCallback;
        this.isImportTasksMode = isImportTasksMode;
        this.canSelectAllInCustomer = canSelectAllInCustomer;
        this.canSelectAllInProject = canSelectAllInProject;

        this.rpc = null;
        this.desiredPortionSize = 100;
        this.maxResultsPerPortion = 150;

        this.preSelectedProjectIdsSet = {};
        this.preSelectedCustomerIdsSet = {};

        this.state = {
            preSelectedTaskIds: [],
            preSelectedProjectIds: [],
            preSelectedCustomerIds: [],
            customerProjectSelection: {
                customerId: null,
                projectId: null
            },
            allSelected: false,
            allSelectedDisabled: false,
            selectedTaskIds: [],
            user: null
        }

        this.onStateChanged(({user, preSelectedTaskIds}, prevState) => {
            if (user !== prevState.user || preSelectedTaskIds !== prevState.preSelectedTaskIds) {
                this._updateTaskListController()
            }
        })
        this.onStatePropertyDeeplyChanged("preSelectedProjectIds", ids => this.preSelectedProjectIdsSet = arrayToSet(ids));
        this.onStatePropertyDeeplyChanged("preSelectedCustomerIds", ids => this.preSelectedCustomerIdsSet = arrayToSet(ids));
        this.onStatePropertyDeeplyChanged("customerProjectSelection",
            customerProjectSelection => this._updateCustomerProjectSelection(customerProjectSelection));
        this.onStatePropertyDeeplyChanged("allSelected", allSelected => this._updateAllSelected(allSelected));
        this.onStatePropertyDeeplyChanged("allSelectedDisabled", allSelected => this._updateAllSelected(allSelected));
        this.onStateChanged(() => this._updateApplyButtonEnabled());
    }

    afterRender() {
        super.afterRender();

        this.$panel = this.$el;
        this.$applyButton = this.$panel.find("." + this.getApplyButtonClass());
        this.$selectAllCheckbox = this.$panel.find(".selectAll input");
        this.$selectAllLabel = this.$panel.find(".selectAllLabel");

        this.$selectAllCheckbox.click(() => this.setState({allSelected: !this.state.allSelected}));

        this.initCpSelectorController();
        this.initTaskListController();

        this.$applyButton.click(() => this.onApplyButtonClick());

        setMouseDownOutsideHandler(this.$panel, event => this.onMouseDownOutside(event));

        this.$panel.find(".selectorFilterButton").click(() => this.onFilterButtonClick());
        this.$panel.find(".filterPanel .closeButton").click(() => this.onFilterCloseButtonClick());
        this.$panel.find(".selectAllButton").click(() => this.onSelectAllButtonClick());
        this.$panel.find(".unselectAllButton").click(() => this.onUnselectAllButtonClick());

        this.$el.on('click', (e) => {
            e.stopPropagation()
        })
    }

    initCpSelectorController() {
        this.cpSelectorController = new CPSelectorController(
            this.$panel.find(".customerProjectSelector.activeProjects"),
            (customerId, projectId, customer, project) => {
                const directlySelected = !!(customerId && this.preSelectedCustomerIdsSet[customerId]) ||
                                         !!(projectId && this.preSelectedProjectIdsSet[projectId])

                const selectedByParent = !!(projectId && this.preSelectedCustomerIdsSet[project.customerId])

                this.setState({
                    customerProjectSelection: {customerId, projectId},
                    allSelected: directlySelected || selectedByParent,
                    allSelectedDisabled: directlySelected || selectedByParent
                })
            });
    }

    createTaskListController(params) {
        // Abstract
    }

    initTaskListController() {
        const taskListScroll = ScrollUtils.addVerticalScroll(this.$panel.find(".taskList"))
        this.taskListController = this.createTaskListController({
            container: this.$panel.find(".taskList .content"),
            searchField: this.$panel.find(".filterField"),
            initialRequestSpinnerPlaceholder: this.$panel.find(".spinnerPlaceholder"),
            isOwnTimeTrack: true,
            isRecentTasksMode: false,
            isImportTasksMode: this.isImportTasksMode,
            desiredPortionSize: this.desiredPortionSize,
            maxResultsPerPortion: this.maxResultsPerPortion,
            isMultiSelect: true,
            onSelectionChange: () => this.setState({selectedTaskIds: this.taskListController.getSelectedTaskIds()}),
            disableAlreadyAdded: true,
            onRequestProcessingComplete: (data) => {
                ScrollUtils.refreshCustomScroll(taskListScroll)
            },
        });
    }

    templateHtml() {
        let tableRowTemplates =
            "            <div class='templates' style='display: none'>" +
            "                <div class='row task'>" +
            "                    <table cellpadding='0' cellspacing='0' style='width: 100%'>" +
            "                        <tr>" +
            "                            <td class='checkboxCol'>" +
            "                                <input type='checkbox'>" +
            "                            </td>" +
            "                            <td class='infoCol'>" +
            "                                <span class='taskId' style='display: none'>#</span>" +
            "                                <span class='taskName'>#</span>" +
            "                                <span class='alreadyAdded' style='display: none'><%-alreadyAdded%></span>" +
            "                                <br>" +
            "                                <span class='details'>#</span>" +
            "                            </td>" +
            "                        </tr>" +
            "                    </table>" +
            "                </div>" +
            "                <div class='row showMore last'>" +
            "                    <div class='panelDiv'>" +
            "                        <div class='resultsContainDiv'>#</div>" +
            "                        <div class='showMoreLinkDiv'>" +
            "                            <span class='showMoreLink'><%-showMore%></span>" +
            "                        </div>" +
            "                    </div>" +
            "                </div>" +
            "            </div>";

        let selectTasksPanel =
            "        <div style='margin-top: 12px; margin-bottom: 8px'>" +
            "            <table cellpadding='0' cellspacing='0' style='width: 100%'>" +
            "                <tr>" +
            "                    <td style='white-space: nowrap' class='label'>" +
            "                        <div class='selectTasksLabel'>" +
            "                            <%-selectTasks%>" +
            "                        </div>" +
            "                        <div class='spinnerPlaceholder'></div>" +
            "                    </td>" +
            "                    <td style='width: 195px; text-align: right;'>" +
            "                   " + this.filterPanelTemplateHtml() +
            "                    </td>" +
            "                </tr>" +
            "            </table>" +
            "        </div>";

        return "<div class='taskSelectorPanel addTasksPanel hidden'>" +
               "    <div class='addTasksSelectorPanelContent'>" +
               "        " + this.topPanelTemplateHtml() +
               "        <div class='titleContainer label'>" +
               "            <span class='selectCustomerProjectLabel'><%-selectProjectOrCustomer%></span>" +
               "            " + this.selectCpSectionAdditionalControlsTemplateHtml() +
               "        </div>" +
               "        " + this.cpSelectorControllerContainerTemplateHtml() +
               "        " + selectTasksPanel +
               "        <div class='selectAllContainer'>" +
               "            <div class='selectAll'><label><input type='checkbox'/><span class='selectAllLabel'></span></label></div>" +
               "        </div>" +
               "        <div class='taskListContainer'>" +
               "            <div class='taskList' style='position: relative'>" +
               "                <div class='content'>" +
               "                    <div class='pleaseSelectCustomerOrProject'>" +
               "                        <%-pleaseSelectCustomerOrProject%>" +
               "                    </div>" +
               "                    <div>" +
               "                        <table cellpadding='0' cellspacing='0' style='width: 100%'>" +
               "                            <tr>" +
               "                                <td class='noMatches' style='display: none; max-width: 100px'>#</td>" +
               "                            </tr>" +
               "                        </table>" +
               "                    </div>" +
               "                    " + tableRowTemplates +
               "                </div>" +
               "            </div>" +
               "            <div class='moire'></div>" +
               "        </div>" +
               "        <div class='bottom'>" +
               "            " + this.bottomPanelTemplateHtml() +
               "        </div>" +
               "    </div>" +
               "</div>"
    }

    selectCpSectionAdditionalControlsTemplateHtml() {
        return "";
    }

    filterPanelTemplateHtml() {
        return "<div style='position: relative'>" +
               "    <button class='selectorFilterButton' disabled></button>" +
               "    <div class='filterPanel' style='display: none'>" +
               "        <input maxlength='<%=maxNameLength%>' class='filterField' type='text' autocomplete='off'>" +
               "        <button class='closeButton'></button>" +
               "    </div>" +
               "</div>";
    }

    bottomPanelTemplateHtml() {
        return "<button class='addSelectedButton' disabled><%-addSelected%></button>";
    }

    topPanelTemplateHtml() {
        return ""
    }

    static cpSelectorControllerTemplateHtml() {
        return "<div class='customerProjectSelector #divClassName'>" +
               "    <table cellpadding='0' cellspacing='0' class='combobox'>" +
               "        <tr>" +
               "            <td><input type='text' maxlength='<%=maxNameLength%>' autocapitalize='off' autocorrect='off' autocomplete='off' spellcheck='false'></td>" +
               "            <td class='dropdownButton'>&nbsp;</td>" +
               "        </tr>" +
               "    </table>" +
               "    <div class='cpDropdown hidden'>" +
               "        <div class='cpList'>" +
               "            <div class='noEntriesMsg'><%-cpSelectorEmptyActive%></div>" +
               "            <div class='noMatchesMsg'><%-cpSelectorNothingFoundActive%></div>" +
               "            <div class='templates' style='display: none'>" +
               "                <table class='row customer' cellpadding='0' cellspacing='0'>" +
               "                    <tr>" +
               "                        <td class='name'>#</td>" +
               "                        <td class='expandCollapseCell'><button class='expandCollapse'></button></td>" +
               "                    </tr>" +
               "                </table>" +
               "                <div class='row project' style='display: none'>" +
               "                    <span class='name'>#</span>" +
               "                </div>" +
               "                <div class='projectsTrailer'></div>" +
               "            </div>" +
               "        </div>" +
               "        <div class='bottomBar'>" +
               "            <button class='expandAll'><%-expandAll%></button>" +
               "            <button class='collapseAll'><%-collapseAll%></button>" +
               "        </div>" +
               "    </div>" +
               "</div>";
    }

    cpSelectorControllerContainerTemplateHtml() {
        let cpSelectorControllerTemplate = BaseTaskSelector.cpSelectorControllerTemplateHtml();
        let cpSelectorController = cpSelectorControllerTemplate.replace("#divClassName", "activeProjects");
        return "<div>" + cpSelectorController + "</div>";
    }

    templateModel() {
        return {
            maxNameLength: EntityTextLimits.MAX_CPT_NAME,
        }
    }

    messageKeys() {
        return {
            selectProjectOrCustomer: "submit_tt.task_selector.select_customer_or_project",
            selectTasks: "submit_tt.task_selector.select_tasks",
            pleaseSelectCustomerOrProject: "submit_tt.task_selector.to_view_tasks",
            expandAll: "submit_tt.task_selector.expand_all",
            collapseAll: "submit_tt.task_selector.collapse_all",
            cpSelectorEmptyActive: "submit_tt.cp_selector.empty.active",
            cpSelectorNothingFoundActive: "submit_tt.cp_selector.nothing_found.active",
            showMore: "submit_tt.task_search.show_more",
            alreadyAdded: "submit_tt.task_search.already_added",
            addSelected: "submit_tt.task_selector.add_selected",
        }
    }

    getApplyButtonClass() {
        return "addSelectedButton";
    }

    showCpSelectorController(needReinit) {
        if (!this.cpSelectorController.isInitialized() && this.rpc === null || needReinit) {
            this.cpSelectorController.spin();
            this.rpc = this._getCPListRpcCall(
                data => this.onCustomersProjectsRequestComplete(data),
                (jqXHR, textStatus, errorThrown) => this.onCustomersProjectsRequestError(jqXHR, textStatus, errorThrown));
        }
    }

    _getCPListRpcCall(onSuccess, onError) {
        // Abstract
    }

    showPanel(positionParams) {
        if (this.$panel.hasClass('hidden')) {
            this.$panel.removeClass('hidden');

            this.showCpSelectorController(true);
            KeyDownHandler.setOnKeyDownHandler(this.onKeyDown);

            if (this.$scrollableContainerElement)
                this.$scrollableContainerElement.one("scroll", () => this.hidePanel());

            showElementAt(this.$panel, positionParams);

            smoothlyScrollDownToElement(this.$panel, 5);
        }
    }

    hidePanel() {
        if (!this.$panel.hasClass('hidden')) {
            this.$panel.addClass('hidden');
            KeyDownHandler.restoreOnKeyDownHandler();
            this.cpSelectorController.closeDropdown();
            this.onHideCallback()
        }
    }

    hide() {
        this.hidePanel()
    }

    openFilter() {
        this.$panel.find(".filterPanel").show();
        this.$panel.find(".filterField").focus();
    }

    closeFilter(skipUpdate) {
        this.$panel.find(".filterPanel").hide();
        this.taskListController.clearQuery(skipUpdate);
    }

    isFilterOpen() {
        return this.$panel.find(".filterPanel:visible").length === 1;
    }

    onApplyButtonClick() {
        if (!this.$applyButton.hasClass("disabled")) {
            this.selectionCallback(this._getSelection(), this.taskListController.getLoadedTaskList());
        }
    }

    _getSelection() {
        let selectAllCheckBoxDisabled = this.$selectAllCheckbox.attr('disabled');
        return {
            customerId: this.state.allSelected && !selectAllCheckBoxDisabled && this.state.customerProjectSelection.customerId ? this.state.customerProjectSelection.customerId : null,
            projectId: this.state.allSelected && !selectAllCheckBoxDisabled && this.state.customerProjectSelection.projectId ? this.state.customerProjectSelection.projectId : null,
            taskIds: !this.state.allSelected ? this.state.selectedTaskIds : null
        }
    }

    onMouseDownOutside(event) {
        this.hidePanel();
    }

    /**
     * @param data
     * @config customers
     * @config projects
     */
    onCustomersProjectsRequestComplete(data) {
        this.cpSelectorController.init(data, true);
        this.cpSelectorController.stopSpin();
    }

    onCustomersProjectsRequestError(jqXHR, textStatus, errorThrown) {
        this.cpSelectorController.stopSpin();
        this.hidePanel();
        this.rpc = null;

        if (errorThrown.exception === 'com.actimind.actitime.administration.NoSuchUserException') {
            document.location.reload();
            return true;
        }

        return false;
    }

    onFilterButtonClick() {
        this.openFilter();
    }

    onFilterCloseButtonClick() {
        this.closeFilter();
    }

    onSelectAllButtonClick() {
        this.taskListController.selectAll();
    }

    onUnselectAllButtonClick() {
        this.taskListController.unselectAll();
    }

    onKeyDown = (event) => {
        event = event || window.event;
        if (event.keyCode === KEY_CODE_ESC) {
            if (this.isFilterOpen())
                this.closeFilter();
            else
                this.hidePanel();
        }
    }

    _updateTaskListController() {
        const user = this.state.user;
        this.taskListController.setData(
            user ? user.id : -1,
            user ? UserNameFormat.formatFML(user.firstName, user.lastName, user.middleName) : '',
            this.state.preSelectedCustomerIds,
            this.state.preSelectedProjectIds,
            this.state.preSelectedTaskIds,
        )
    }

    _updateCustomerProjectSelection({customerId, projectId}) {
        this.closeFilter(true);

        if (customerId || projectId) {
            this.$panel.find(".pleaseSelectCustomerOrProject").hide();
            this.$panel.find(".selectorFilterButton").prop('disabled', false);
            this.taskListController.makeInitialRequest(customerId, projectId);
        } else {
            this.taskListController.clear();
            this.$panel.find(".pleaseSelectCustomerOrProject").show();
            this.$panel.find(".selectorFilterButton").prop('disabled', true);
        }

        let allSelectedAllowed = false;
        if (this.canSelectAllInCustomer && !!customerId) {
            this.$selectAllLabel.text(this._selectAllForCustomerMessage());
            allSelectedAllowed = true;
        } else if (this.canSelectAllInProject && !!projectId) {
            this.$selectAllLabel.text(this._selectAllForProjectMessage());
            allSelectedAllowed = true;
        }
        this.$panel.toggleClass("allSelectedAllowed", allSelectedAllowed);
    }

    _updateAllSelected() {
        const {allSelected, allSelectedDisabled} = this.state
        if (allSelected) {
            this.$selectAllCheckbox.attr("checked", "checked")
            this.taskListController.selectAll()
        }
        else {
            this.$selectAllCheckbox.removeAttr("checked")
            if (this.taskListController.isSelectAllRows()) {
                this.taskListController.unselectAll()
            }
        }
        this.$selectAllCheckbox.attr('disabled', allSelectedDisabled)
        this.$panel.toggleClass("allSelected", allSelected);
    }

    _isButtonEnabled() {
        return this.state.allSelected || this.state.selectedTaskIds.length !== 0
    }

    _updateApplyButtonEnabled() {
        const applyButtonDisabled = !this._isButtonEnabled()
        this.$applyButton.toggleClass("disabled", applyButtonDisabled);
        this.$applyButton.prop("disabled", applyButtonDisabled);
    }

    _selectAllForCustomerMessage() {
        return messageResource.getMessage("task_selector.select_all_for_customer")
    }

    _selectAllForProjectMessage() {
        return messageResource.getMessage("task_selector.select_all_for_project")
    }
}



// WEBPACK FOOTER //
// ./web/js/user/userlist/task/BaseTaskSelector.js